import { gradingIdSchema } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { UIMessage } from "ai";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { v4 as uuid } from "uuid";
import { z } from "zod";

const knowledgeBaseAssistantPrefillSchema = z.object({
  gradingId: gradingIdSchema,
  statementIndex: z.int().min(0),
});

/** Prefill data for the knowledge base assistant */
export type KnowledgeBaseAssistantPrefill = z.infer<
  typeof knowledgeBaseAssistantPrefillSchema
>;

/** Parse the prefill query param from the URL once and clear it */
export function usePrefillFromUrl(): KnowledgeBaseAssistantPrefill | null {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse the prefill query param once on mount using useState lazy initializer.
  // This ensures the value persists even after the URL is cleared.
  const [prefill] = useState<KnowledgeBaseAssistantPrefill | null>(() => {
    const prefillParam = searchParams.get("prefill");
    if (!prefillParam) return null;

    try {
      return knowledgeBaseAssistantPrefillSchema.parse(
        JSON.parse(prefillParam),
      );
    } catch {
      return null;
    }
  });

  // Clear the prefill param from the URL after mount to prevent re-sending on refresh.
  // Use a ref to ensure this only happens once.
  const hasClearedPrefillRef = useRef(false);
  useEffect(() => {
    if (!hasClearedPrefillRef.current && searchParams.has("prefill")) {
      hasClearedPrefillRef.current = true;
      setSearchParams(
        (prev) => {
          prev.delete("prefill");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  return prefill;
}

/** Build an initial assistant message that asks the user what they want to do with the finding */
export function buildInitialAssistantMessage(
  prefill: KnowledgeBaseAssistantPrefill,
): UIMessage {
  return {
    id: `prefill-assistant-${uuid()}`,
    role: "assistant",
    parts: [
      {
        type: "text",
        text: `I can help you update your knowledge base based on the finding (grading id: ${prefill.gradingId}, statement index: ${prefill.statementIndex}).

What's the issue with this knowledge base finding?
1. I want to prevent findings like this in the future because it's not relevant or overly nitpicky
2. There's inaccurate or out of date information in my KB that I want to update
3. Something else

If you specify which category of change you want to make, and give me some specifics of what's wrong with this finding, I can draft some updates for you.`,
      },
    ],
  };
}
